import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Lock } from "lucide-react";

const OtpVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const [email, setEmail] = useState(state.email || "");
  const [password, setPassword] = useState(state.password || "");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(state.message || "");

  const getBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const backendRoot = process.env.REACT_APP_BACKEND;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    return (backendUrl || apiBase || backendRoot || "http://localhost:8000")
      .trim()
      .replace(/([^:]\/)\/+/g, "$1")
      .replace(/\/+$/, "");
  };

  const extractErrorMessage = (data, fallback) => {
    if (typeof data === "string" && data.trim()) return data;
    if (Array.isArray(data?.message) && data.message.length) {
      return data.message.join(", ");
    }
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }
    if (typeof data?.error === "string" && data.error.trim()) {
      return data.error;
    }
    return fallback;
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");

    const normalizedEmail = email.trim().toLowerCase();
    const enteredOtp = otp.trim();

    if (!normalizedEmail) {
      setError("Please enter your registered email.");
      return;
    }

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!validEmail) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!enteredOtp) {
      setError("Please enter OTP.");
      return;
    }

    setLoading(true);
    try {
      const baseUrl = getBaseUrl();
      const verifyRes = await fetch(`${baseUrl}/customers/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail, otp: enteredOtp }),
      });
      const verifyData = await verifyRes.json().catch(() => ({}));
      if (!verifyRes.ok) {
        throw new Error(extractErrorMessage(verifyData, "OTP verification failed"));
      }

      if (password) {
        const loginRes = await fetch(`${baseUrl}/customers/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail, password }),
        });
        const loginData = await loginRes.json().catch(() => ({}));
        if (!loginRes.ok) {
          throw new Error(extractErrorMessage(loginData, "Login failed after OTP"));
        }

        if (loginData?.access_token) {
          localStorage.setItem("token", loginData.access_token);
        }
        localStorage.setItem(
          "user",
          JSON.stringify({ email: normalizedEmail, id: loginData?.id, name: loginData?.name })
        );
        window.dispatchEvent(new Event("userUpdated"));
      }

      setMessage("OTP verified successfully.");
      navigate("/login");
    } catch (err) {
      setError(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResendOtp = async () => {
    setError("");
    setMessage("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Please enter your registered email.");
      return;
    }

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!validEmail) {
      setError("Please enter a valid email address.");
      return;
    }

    setResendLoading(true);
    try {
      const baseUrl = getBaseUrl();
      const res = await fetch(`${baseUrl}/customers/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(extractErrorMessage(data, "Failed to resend OTP."));
      }

      if (data?.already_verified) {
        setMessage(data?.message || "Account already verified. Please login.");
        setResendCooldown(0);
        return;
      }

      if (data?.otp_sent === false) {
        throw new Error(extractErrorMessage(data, "OTP resend failed."));
      }

      setMessage(data?.message || "OTP resent successfully.");
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || "Failed to resend OTP.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <h2 className="text-2xl font-bold text-center text-[#1F3C88]">Verify OTP</h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          Enter OTP sent to your email
        </p>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="text-sm text-gray-600">Email address</label>
            <div className="relative mt-1">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                className="w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-600">OTP</label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
              className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
            />
            <div className="mt-2 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendLoading || resendCooldown > 0}
                className="text-[#1F3C88] font-semibold hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {resendLoading
                  ? "Resending..."
                  : resendCooldown > 0
                    ? `Resend OTP in ${resendCooldown}s`
                    : "Resend OTP"}
              </button>
              <span className="text-gray-400">Didn&apos;t receive OTP?</span>
            </div>
          </div>

          {password && (
            <div>
              <label className="text-sm text-gray-600">Password</label>
              <div className="relative mt-1">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}
          {message && <p className="text-sm text-green-600">{message}</p>}

          <button
            disabled={loading}
            className="w-full bg-[#1F3C88] text-white py-2.5 rounded-lg font-semibold disabled:opacity-60"
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-600 mt-4">
          <Link to="/login" className="text-[#1F3C88] font-semibold hover:underline">
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default OtpVerify;
